import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type SearchMetadataTone = "neutral" | "info" | "success";

const toneClassNames: Record<
      SearchMetadataTone,
      {
            outer: string;
            inner: string;
            interactive: string;
      }
> = {
      neutral: {
            outer: "border border-brand-ink/[0.08] bg-card text-muted-foreground",
            inner: "bg-brand-ink/[0.02] text-muted-foreground",
            interactive: "hover:border-brand-ink/14",
      },
      info: {
            outer: "border border-info/20 bg-card text-info",
            inner: "bg-info-light text-info",
            interactive: "hover:bg-info-light/60",
      },
      success: {
            outer: "border border-success/20 bg-card text-success",
            inner: "bg-success-light text-success",
            interactive: "hover:bg-success-light",
      },
};

export function SearchMetadataPill({
      icon: Icon,
      label,
      value,
      tone = "neutral",
      trailing,
      truncateValue = false,
      interactive = false,
      className,
}: {
      icon: LucideIcon;
      label: string;
      value: ReactNode;
      tone?: SearchMetadataTone;
      trailing?: ReactNode;
      truncateValue?: boolean;
      interactive?: boolean;
      className?: string;
}) {
      const toneClasses = toneClassNames[tone];

      return (
            <div
                  className={cn(
                        "flex w-fit items-center gap-1.5 rounded-full pl-2",
                        toneClasses.outer,
                        interactive && "cursor-pointer transition-colors",
                        interactive && toneClasses.interactive,
                        className,
                  )}
            >
                  <span className="flex items-center gap-1 text-xs font-medium">
                        <Icon className="h-3.5 w-3.5 shrink-0" /> {label}
                  </span>
                  <div className={cn("flex w-fit items-center gap-1.5 rounded-full px-2 py-1", toneClasses.inner)}>
                        <span className={cn("text-xs font-medium", truncateValue && "max-w-[280px] truncate")}>
                              {value}
                        </span>
                  </div>
                  {trailing ? <span className="mr-1.5 shrink-0">{trailing}</span> : null}
            </div>
      );
}
